// HILDR — fempunktsordre-generator (Forsvarets 5-punkts ordre / SMEAC), spesialisert
// for FORFLYTNING. Deterministisk: dra en enhet → ordren skrives automatisk, så
// ordregangen (OODA: Orient→Decide) går raskt. HIMMELRETNINGER/«IKKE» i STORE
// bokstaver via stridsEmphasis (samme doktrine som stridsmeldinger).
//
// Wire-/collab-skjema hører hjemme i @skade/contracts når ordren sendes på nettet
// (fase 3) — her er ren logikk + typer.
import { buildDtg, stridsEmphasis } from '@skade/contracts';
import { planMovement, METHODS } from './movement.js';
const loc = (mgrs, p) => mgrs && mgrs.trim() ? mgrs.trim() : `${p.lat.toFixed(4)}, ${p.lon.toFixed(4)}`;
const hhmm = (ms) => new Date(ms).toISOString().slice(11, 16) + 'Z';
/** Bygg en komplett fempunktsordre for en forflytning. Deterministisk. */
export function buildMovementOrder(input) {
    const sone = input.sone ?? 'A';
    const plan = planMovement(input.from, input.to, input.method);
    const spec = METHODS[input.method];
    const fra = (input.issuer ?? 'TOC').trim();
    const fromTxt = loc(input.fromMgrs, input.from);
    const toTxt = loc(input.toMgrs, input.to);
    const ankomstTsMs = input.startTsMs + plan.etaMin * 60_000;
    const intent = (input.intent ?? `innta ny posisjon ved ${toTxt} og være klar for videre oppdrag`).trim();
    const oppdrag = stridsEmphasis(`${input.unit.label} forflytter seg ${spec.label.toLowerCase()} fra ${fromTxt} til ${toTxt} ` +
        `(retning ${plan.sector}, ${plan.distanceKm} km), oppstart ${hhmm(input.startTsMs)}, ` +
        `for å ${intent}.`);
    const situasjon = {
        fiende: stridsEmphasis(input.enemy?.trim() || 'Ingen kjent fiendtlig kontakt langs ruten. Vær årvåken i sektor ' + plan.sector + '.'),
        egne: (input.friendly?.trim() || 'Naboavdelinger uendret. Høyere enhets hensikt: opprettholde bevegelsesfrihet.'),
        sivileForhold: (input.civil?.trim()
            || 'Sivil ferdsel må forventes langs ruten. Unngå å sperre gjennomkjøring; '
                + 'ta hensyn til nødetater. Meld sivile observasjoner av oss videre til TOC.'),
        forsterkninger: (input.attachments?.trim() || 'Ingen forsterkninger eller avgivelser for denne forflytningen.'),
        terrengVaer: `Rute ~${plan.distanceKm} km mot ${plan.sector}. Se kart. Estimert marsjtid ${plan.etaMin} min i ${plan.speedKmh} km/t.`,
    };
    const koordinering = [
        `Oppstart ${hhmm(input.startTsMs)} — beregnet ankomst ${hhmm(ankomstTsMs)} (${plan.etaMin} min).`,
        `Meld inn ved oppstart, faslinjer og ankomst. Bekreft «I POSISJON» ved framme.`,
        stridsEmphasis(input.actionsOnContact?.trim() || 'Ved kontakt: stopp, meld, søk dekning, avvent ordre — IKKE bryt ut uten klarering.'),
        stridsEmphasis(input.roe?.trim()
            || 'Engasjementsregler: gjeldende RoE uendret. Ildåpning KUN ved selvforsvar '
                + 'eller etter klarering fra høyere sjef. Positiv identifikasjon FØR ild.'),
    ];
    // Oppdrag til underavdelinger: doktrinen krever egne oppdrag, ikke bare et
    // felles konsept. Uten oppgitte oppdrag settes marsjorden, som ER oppdraget
    // for en forflytning — men det sies eksplisitt, ikke stilltiende.
    const underavdelinger = (input.subUnitTasks?.map((t) => t.trim()).filter(Boolean).length
        ? input.subUnitTasks.map((t) => stridsEmphasis(t.trim()))
        : ['Marsjorden gjelder som oppdrag: tropps-/lagssjefer holder rekkefølge, avstand og meldeplikt.']);
    return {
        ordreId: `FO-${input.unit.id}-${input.startTsMs}`,
        type: 'forflytning',
        dtg: buildDtg(input.startTsMs, sone),
        fra,
        til: input.unit.label,
        situasjon,
        oppdrag,
        utforelse: {
            hensikt: stridsEmphasis(intent.charAt(0).toUpperCase() + intent.slice(1)),
            konsept: spec.konsept,
            faser: ['1. Klargjøring og oppstilling', `2. Forflytning ${spec.label.toLowerCase()} mot ${plan.sector}`, '3. Innrykk og sikring av ny posisjon'],
            underavdelinger,
            koordinering,
        },
        adminForsyning: input.method === 'kolonne' || input.method === 'kjoretoy'
            ? 'Drivstoff fylt før avmarsj. MEDEVAC-plan aktiv. Ammunisjon og forpleining etter oppsett.'
            : input.method === 'heli'
                ? 'Vekt/last bekreftet før avgang. MEDEVAC via samme luftressurs. Utstyr sikret for flyging.'
                : 'Vann/forpleining for marsjtid. MEDEVAC-plan aktiv. Bær kun stridsnødvendig materiell.',
        ledelseSamband: `Samband: ${input.net?.trim() || 'primærnett TOC'}${input.callsign ? ` · kallesignal ${input.callsign.trim()}` : ''}. ` +
            `Ledelse: ${input.kp?.trim() || 'KP hos TOC'}. Nestkommando fastsatt. `
            + `Kjennetegn/passord: ${input.recognition?.trim() || 'etter gjeldende ordre for perioden'}. `
            + 'Kvitter på denne ordren.',
        movement: {
            ...plan,
            from: { lat: input.from.lat, lon: input.from.lon },
            to: { lat: input.to.lat, lon: input.to.lon },
            fromMgrs: input.fromMgrs, toMgrs: input.toMgrs,
            startTsMs: input.startTsMs, ankomstTsMs,
        },
    };
}
/** Kort én-linjes sammendrag (for kart-popup / logg). */
export function orderSummary(o) {
    return `${o.dtg} · ${o.til} → ${o.movement.sector} ${o.movement.distanceKm} km (${o.movement.methodLabel}, ETA ${o.movement.etaMin} min)`;
}
//# sourceMappingURL=order.js.map